import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useRouter } from 'vue-router';

import useAuth from '@/composables/useAuth';
import useSeats from '@/composables/useSeats';
import { useBillingStore } from '@/stores';

export default function useTeamInvites() {
  const { hasScope } = useAuth();
  const canEditUsers = computed(() => hasScope('write:members'));

  const {
    isFreeTier,
    hasSubscription,
    isSubscriptionCancelled,
    isOnFreeTrial,
    hasPaymentIssue,
  } = storeToRefs(useBillingStore());
  const {
    atSeatLimit,
    mustTalkToSales,
    integrationSeatsRemaining,
    canUseIntegrationSeats,
  } = useSeats();

  /* True when the org has integration capacity (either a plan with remaining seats,
     or no plan at all which the composable treats as unlimited). Mirrors the modal's
     own visibility logic — if integration would appear as a usable option inside the
     modal, the team page button should not be blocked. */
  const hasIntegrationSeatsAvailable = computed(
    () => canUseIntegrationSeats.value && integrationSeatsRemaining.value > 0,
  );

  const router = useRouter();
  const invitesDisabledReason = computed(() => {
    if (!canEditUsers.value)
      return 'You do not have permission to send invites';
    if (!atSeatLimit.value || hasIntegrationSeatsAvailable.value) return null;
    if (
      hasSubscription.value &&
      hasPaymentIssue.value &&
      !mustTalkToSales.value &&
      !isSubscriptionCancelled.value
    )
      return 'Update your payment method to add seats';
    if (!isSubscriptionCancelled.value)
      return 'Unblock your team by adding more seats to Crossbeam';
    return 'Your subscription is cancelled and you are at your seat limit';
  });
  const inviteTooltipButtonText = computed(() => {
    if (!isFreeTier.value && hasSubscription.value && !isOnFreeTrial.value)
      return 'Add seats';
    if (!isFreeTier.value && !hasSubscription.value && !isOnFreeTrial.value)
      return 'Talk to Sales';
    return 'Upgrade';
  });
  const hideUpsellCta = computed(
    () =>
      hasSubscription.value &&
      hasPaymentIssue.value &&
      !mustTalkToSales.value &&
      inviteTooltipButtonText.value === 'Add seats',
  );
  async function handleInvite() {
    await router.push({ name: 'invite_user' });
  }
  return {
    invitesDisabledReason,
    inviteTooltipButtonText,
    handleInvite,
    mustTalkToSales,
    hideUpsellCta,
  };
}
